import React from 'react';

const PricingInfo = () => {
  return (
    <div className="bg-white rounded-lg shadow-lg p-6" data-testid="pricing-info">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">💰 Transparent Pricing</h2>
      
      <div className="space-y-6">
        {/* Time Restrictions Notice */}
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-red-800 mb-2">⏰ Ordering Deadlines</h3>
          <div className="text-sm text-red-700 space-y-1">
            <p>• <strong>Lunch:</strong> Must order before 7:30 AM on day of service</p>
            <p>• <strong>Dinner:</strong> Must order by 11:00 AM on day of service</p>
          </div>
        </div>

        {/* Chef Service Fees - 2 & 4 People */}
        <div>
          <h3 className="text-lg font-semibold text-orange-600 mb-3">Chef Service Fees (2-4 People)</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4 bg-orange-50">
              <div className="text-3xl mb-2">🥗</div>
              <h4 className="font-bold text-lg">Lunch</h4>
              <div className="space-y-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm">2 people:</span>
                  <span className="text-xl font-bold text-orange-600">$175</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">4 people:</span>
                  <span className="text-xl font-bold text-orange-600">$400</span>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-3">+ $26 processing fee + groceries</p>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
              <div className="text-3xl mb-2">🍽️</div>
              <h4 className="font-bold text-lg">Dinner</h4>
              <div className="space-y-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm">2 people:</span>
                  <span className="text-xl font-bold text-blue-600">$425</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">4 people:</span>
                  <span className="text-xl font-bold text-blue-600">$825</span>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-3">+ $26 processing fee + groceries</p>
            </div>
          </div>
        </div>

        {/* 5-9 People Pricing */}
        <div className="border border-gray-200 rounded-lg p-4 bg-purple-50">
          <h3 className="text-lg font-semibold text-purple-800 mb-2">👥 5-9 People (Lunch or Dinner)</h3>
          <p className="text-2xl font-bold text-purple-600 mb-2">$600 - $1,200</p>
          <p className="text-sm text-gray-700">
            Price varies based on meal complexity
          </p>
          <p className="text-xs text-gray-600 mt-2">+ $26 processing fee + groceries</p>
        </div>

        {/* 10-30 People Pricing */}
        <div className="border border-gray-200 rounded-lg p-4 bg-green-50">
          <h3 className="text-lg font-semibold text-green-800 mb-2">🎉 10-30 People (Catering)</h3>
          <p className="text-2xl font-bold text-green-600 mb-2">$75 - $150 per plate</p>
          <p className="text-sm text-gray-700 font-semibold">
            📞 Must contact office for booking and setup
          </p>
          <p className="text-xs text-gray-600 mt-2">+ groceries (custom quote provided)</p>
        </div>

        {/* Grocery Costs Info */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-yellow-800 mb-3">🛒 Grocery Costs</h3>
          <p className="text-sm text-gray-700 mb-2">
            <strong>Grocery costs are charged separately</strong> based on actual receipts submitted by your chef.
          </p>
          <p className="text-sm text-gray-700">
            The chef will purchase fresh, quality ingredients for your meal and provide receipts. 
            You pay only for what was actually purchased.
          </p>
        </div>

        {/* Example Pricing */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3">📊 Example Bookings</h3>
          
          <div className="space-y-4">
            {/* Example 1: 2 people dinner */}
            <div className="border-b border-gray-200 pb-3">
              <p className="font-semibold text-sm mb-2">2 People - Dinner</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Chef Service:</span>
                  <span className="font-semibold">$425.00</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Processing Fee:</span>
                  <span>$26.00</span>
                </div>
                <div className="flex justify-between text-orange-600 font-bold">
                  <span>Total Due Now:</span>
                  <span>$451.00</span>
                </div>
                <div className="flex justify-between text-yellow-700 text-xs mt-2">
                  <span>+ Groceries (billed separately)</span>
                  <span>~$80-$120</span>
                </div>
              </div>
            </div>
            
            {/* Example 2: 4 people lunch */}
            <div>
              <p className="font-semibold text-sm mb-2">4 People - Lunch</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Chef Service:</span>
                  <span className="font-semibold">$400.00</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Processing Fee:</span>
                  <span>$26.00</span>
                </div>
                <div className="flex justify-between text-orange-600 font-bold">
                  <span>Total Due Now:</span>
                  <span>$426.00</span>
                </div>
                <div className="flex justify-between text-yellow-700 text-xs mt-2">
                  <span>+ Groceries (billed separately)</span>
                  <span>~$100-$150</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What's Included */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">✨ What's Included</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>✅ Professional chef at your location</li>
            <li>✅ Menu planning & preparation</li>
            <li>✅ All cooking equipment provided</li>
            <li>✅ Fresh ingredients purchased by chef</li>
            <li>✅ Full meal service</li>
            <li>✅ Kitchen cleanup</li>
            <li>✅ SafeServ certified chefs only</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PricingInfo;
